import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client/react';
import { makeStyles } from 'tss-react/mui';
import { useRouter } from 'next/router';

const SEARCH = gql`
  query search(
    $searchEvent: Boolean
    $searchActor: Boolean
    $searchArticle: Boolean
    $searchValue: String!
  ) {
    search(
      searchEvent: $searchEvent
      searchActor: $searchActor
      searchArticle: $searchArticle
      searchValue: $searchValue
    ) {
      events {
        id
        label
        startedAt
        endedAt
        published
        lat
        lng
        address
        city
        shortDescription
        url
      }
      actors {
        id
        name
        address
        city
        shortDescription
        activity
        lat
        lng
        url
      }
      articles {
        id
        label
        shortDescription
        url
      }
    }
  }
`;

const useStyles = makeStyles()((theme) => ({
  root: {
    width: 'min(45em, 100%)',
    paddingTop: '1.1em',
    margin: 'auto',
    alignSelf: 'stretch',
    [theme.breakpoints.down('md')]: {
      width: '100%',
      paddingTop: '0.75em',
    },
  },
  inputRoot: {
    backgroundColor: 'white',
    borderRadius: 34,
    paddingTop: 0,
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: 34,
    },
    '& .MuiOutlinedInput-input': {
      paddingTop: 14,
      paddingBottom: 14,
    },
  },
  popupIndicator: {
    transform: 'none',
    marginRight: 2,
  },
  group: {
    marginLeft: 10,
    borderBottom: '1px solid #d9d7d7',
  },
  optionShortDescription: {
    fontSize: 14,
    marginLeft: 14,
    fontStyle: 'italic',
  },
  optionActivity: {
    fontSize: 14,
    marginLeft: 14,
    fontStyle: 'italic',
  },
  option: {
    padding: '0.5em 1em 0.5em 1em',
  },
}));

type SearchOption = {
  id: string | number;
  label: string;
  url: string;
  type: 'Actions' | 'Acteurs' | 'Articles';
  activity?: string;
  shortDescription?: string;
};

const getTextWithSearchValue = (
  originalText: string | undefined,
  inputValue: string,
): React.ReactNode => {
  if (!originalText) return originalText;
  let matchIndex = -1;
  try {
    matchIndex = originalText.toLowerCase().search(inputValue.toLowerCase());
    if (matchIndex !== -1) {
      return [originalText.slice(0, matchIndex)]
        .concat(originalText.slice(matchIndex, matchIndex + inputValue.length))
        .concat(originalText.slice(matchIndex + inputValue.length))
        .map((part, index) => {
          if (index === 1) return <b key={index}>{part}</b>;
          return part;
        });
    }
  } catch (_error) {
    // ignore
  }
  return originalText;
};

const SearchEngine = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<{
    events: any[];
    actors: any[];
    articles: any[];
  } | undefined>();
  const [value, setValue] = useState<string | null>(null);
  const { classes } = useStyles();
  const router = useRouter();

  const { refetch, loading, data: searchResult } = useQuery(SEARCH, {
    variables: {
      searchActor: true,
      searchEvent: true,
      searchArticle: true,
      searchValue: '',
    },
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (searchResult && value !== null) {
      const { events = [], actors = [], articles = [] } = searchResult.search;
      setData({ events, actors, articles });
      if (!open) setOpen(true);
    }
  }, [searchResult]);

  const handleInputChange = useCallback(
    (_evt: React.SyntheticEvent, newValue: string) => {
      setValue(newValue);
      if (newValue.length >= 3) {
        refetch({ searchValue: newValue, searchActor: true, searchEvent: true, searchArticle: true });
      } else {
        setOpen(false);
      }
    },
    [refetch],
  );

  const options = useMemo((): SearchOption[] => {
    if (!data) return [];
    const eventOptions = data.events.map((event) => ({
      ...event,
      type: 'Actions' as const,
    }));
    const actorOptions = data.actors.map((actor) => ({
      ...actor,
      type: 'Acteurs' as const,
      label: actor.name,
    }));
    const articleOptions = data.articles.map((article) => ({
      ...article,
      type: 'Articles' as const,
    }));
    return eventOptions.concat(actorOptions).concat(articleOptions);
  }, [data]);

  const handleClickOption = useCallback(
    (_evt: React.SyntheticEvent, option: SearchOption | null) => {
      if (!option) return;
      if (option.type === 'Actions') {
        router.push(`/evenement/${option.url}`);
      } else if (option.type === 'Acteurs') {
        router.push(`/acteur/${option.url}`);
      } else if (option.type === 'Articles') {
        router.push(`/article/${option.url}`);
      }
    },
    [router],
  );

  const filterOptions = useCallback(() => options, [options]);

  return (
    <Autocomplete<SearchOption>
      id="search-engine"
      clearOnBlur={false}
      classes={{
        root: classes.root,
        inputRoot: classes.inputRoot,
        popupIndicator: classes.popupIndicator,
      }}
      onInputChange={handleInputChange}
      onChange={handleClickOption}
      openOnFocus={false}
      open={open}
      groupBy={(option) => option.type}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, currentvalue) => option.label === currentvalue.label}
      noOptionsText="Pas de résultat"
      options={options}
      filterOptions={filterOptions}
      loading={loading}
      popupIcon={<SearchIcon />}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Rechercher un acteur ou une action"
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option, { inputValue }) => {
        const { label, activity, shortDescription } = option;
        const newLabel = getTextWithSearchValue(label, inputValue);
        const newActivity = getTextWithSearchValue(activity, inputValue);
        const newShortDescription = getTextWithSearchValue(shortDescription, inputValue);
        return (
          <li {...props}>
            <div className={classes.option}>
              <div>
                {newLabel} {newActivity && <div className={classes.optionActivity}>{newActivity}</div>}
              </div>
              {shortDescription && (
                <div className={classes.optionShortDescription}>{newShortDescription}</div>
              )}
            </div>
          </li>
        );
      }}
    />
  );
};

export default SearchEngine;
